/**
 * True when `raw` is a same-origin app path safe to hand to the router.
 *
 * Shared so every consumer of an externally-supplied destination applies the
 * same rules — a second hand-rolled copy is how one of them ends up missing
 * the leading-control-char case.
 */
export function isSafeInternalPath(raw: string): boolean {
  // Reject a leading whitespace or control char (code <= 0x20) first: browsers
  // strip these before URL parsing, so e.g. a leading tab on `\t//evil.com`
  // could otherwise let the host slip through the slash checks below.
  if (raw.charCodeAt(0) <= 0x20) return false;
  return raw.startsWith('/') && !raw.startsWith('//') && !raw.startsWith('/\\');
}

/**
 * Extract a SAFE same-origin path from a `returnUrl` query string, or null.
 *
 * Open-redirect guard for the post-login redirect chain: only a plain absolute
 * path is allowed — never a protocol-relative (`//evil.com`), backslash-tricked
 * (`/\evil.com`), or absolute-URL value, all of which browsers can treat as
 * cross-origin. Used by the WorkOS MCP connect flow, where an unauthenticated
 * user landing on /auth/workos is bounced to /login?returnUrl=<that page> and
 * must resume there after signing in.
 *
 * The sole consumer today is react-router `<Navigate>` (same-origin client-side
 * routing), so this guard is defense-in-depth — but it's written to be safe for
 * a future `window.location` consumer too, including leading-whitespace /
 * control-char tricks that some browsers strip before parsing a URL.
 */
export function safeInternalReturnUrl(search: string): string | null {
  // URLSearchParams.get() already percent-decodes the value, so a payload like
  // `%2F%2Fevil.com` surfaces here as `//evil.com` and is rejected below.
  const raw = new URLSearchParams(search).get('returnUrl');
  if (!raw) return null;
  return isSafeInternalPath(raw) ? raw : null;
}
